import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, Calendar, Plus, AlertCircle, CheckCircle, XCircle, 
  RefreshCw, History
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const REASON_LABELS = {
  health: 'Raison de santé',
  personal_request: 'Demande personnelle',
  office_errand: 'Course de bureau',
  other: 'Autre'
};

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Refusé',
  active: 'Actif',
  expired: 'Expiré',
  cancelled: 'Annulé'
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-slate-100 text-slate-600'
};

const STATUS_ICONS = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  active: CheckCircle,
  expired: History,
  cancelled: XCircle
};

export default function MyScheduleExceptions() {
  const { user } = useAuth();
  const [myEmployee, setMyEmployee] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    adjusted_start_time: '10:00',
    adjusted_end_time: '19:00',
    reason: 'personal_request',
    reason_details: ''
  });

  useEffect(() => {
    fetchMyEmployee();
  }, []);

  useEffect(() => {
    if (myEmployee) {
      fetchMyExceptions();
    }
  }, [myEmployee]);

  const fetchMyEmployee = async () => {
    try {
      // Get all employees and find mine by user_id or email
      const response = await api.get('/employees');
      const employees = response.data;
      
      // Find employee linked to current user
      const mine = employees.find(e => 
        e.user_id === user?.id || e.email === user?.email
      );
      
      if (mine) {
        setMyEmployee(mine);
      } else {
        toast.error("Impossible de trouver votre fiche employé");
      }
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      toast.error("Erreur lors du chargement de vos informations");
    }
  };

  const fetchMyExceptions = async () => {
    if (!myEmployee) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/employees/${myEmployee.id}/schedule-exceptions?include_all=true`);
      setExceptions(response.data);
    } catch (error) {
      console.error('Failed to fetch exceptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!myEmployee) {
      toast.error("Impossible de trouver votre fiche employé");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post(`/employees/${myEmployee.id}/schedule-exception`, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        adjusted_start_time: formData.adjusted_start_time,
        adjusted_end_time: formData.adjusted_end_time,
        reason: formData.reason,
        reason_details: formData.reason_details || null
      });
      
      toast.success("Demande envoyée ! Elle sera examinée par votre responsable.");
      setDialogOpen(false);
      resetForm();
      fetchMyExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (exceptionId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette demande ?")) {
      return;
    }
    
    try {
      await api.delete(`/schedule-exceptions/${exceptionId}`);
      toast.success("Demande annulée");
      fetchMyExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const resetForm = () => {
    setFormData({
      start_date: '',
      end_date: '',
      adjusted_start_time: '10:00',
      adjusted_end_time: '19:00',
      reason: 'personal_request',
      reason_details: ''
    });
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const pendingCount = exceptions.filter(e => e.status === 'pending').length;
  const approvedCount = exceptions.filter(e => ['approved', 'active'].includes(e.status)).length;

  return (
    <div className="p-6 space-y-6" data-testid="my-schedule-exceptions-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Mes Exceptions d'Horaire
          </h1>
          <p className="text-slate-600 mt-1">
            Demandez un ajustement temporaire de vos horaires de travail
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-exception-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Demande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Demander une Exception d'Horaire</DialogTitle>
              <DialogDescription>
                Votre demande sera examinée par le service RH ou votre directeur
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Current Schedule Info */}
              {myEmployee && (
                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-slate-700">Vos horaires actuels :</p>
                  <p className="text-slate-600">
                    {myEmployee.work_start_time || '08:00'} - {myEmployee.work_end_time || '17:00'}
                  </p>
                </div>
              )}

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date début *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Date fin *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    required
                    data-testid="end-date-input"
                  />
                </div>
              </div>

              {/* Adjusted Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adjusted_start_time">Nouvelle heure d'arrivée *</Label>
                  <Input
                    id="adjusted_start_time"
                    type="time"
                    value={formData.adjusted_start_time}
                    onChange={(e) => setFormData({ ...formData, adjusted_start_time: e.target.value })}
                    required
                    data-testid="adjusted-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adjusted_end_time">Nouvelle heure de départ *</Label>
                  <Input
                    id="adjusted_end_time"
                    type="time"
                    value={formData.adjusted_end_time}
                    onChange={(e) => setFormData({ ...formData, adjusted_end_time: e.target.value })}
                    required
                    data-testid="adjusted-end-input"
                  />
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="reason">Motif de la demande *</Label>
                <Select
                  value={formData.reason}
                  onValueChange={(value) => setFormData({ ...formData, reason: value })}
                >
                  <SelectTrigger data-testid="reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="health">Raison de santé</SelectItem>
                    <SelectItem value="personal_request">Demande personnelle</SelectItem>
                    <SelectItem value="office_errand">Course de bureau</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reason Details */}
              <div className="space-y-2">
                <Label htmlFor="reason_details">Précisions (optionnel)</Label>
                <Textarea
                  id="reason_details"
                  value={formData.reason_details}
                  onChange={(e) => setFormData({ ...formData, reason_details: e.target.value })}
                  placeholder="Expliquez brièvement la raison de votre demande..."
                  rows={3}
                  data-testid="reason-details-input"
                />
              </div>

              {/* Info Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Votre demande sera examinée par le service RH ou votre directeur. 
                  Vous serez notifié de la décision.
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)} 
                  className="flex-1"
                  disabled={submitting}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={submitting}
                  data-testid="submit-exception-btn"
                >
                  {submitting ? 'Envoi...' : 'Envoyer la Demande'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Approuvées</p>
                <p className="text-2xl font-bold">{approvedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-full">
                <History className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total demandes</p>
                <p className="text-2xl font-bold">{exceptions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exceptions List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Mes Demandes</CardTitle>
            <CardDescription>Historique de vos demandes d'exception d'horaire</CardDescription>
          </div>
          <Button variant="outline" size="icon" onClick={fetchMyExceptions}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : exceptions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Vous n'avez pas encore fait de demande d'exception d'horaire.</p>
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Faire une demande
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Période</TableHead>
                  <TableHead>Horaires demandés</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Décision par</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((exception) => {
                  const StatusIcon = STATUS_ICONS[exception.status] || Clock;
                  return (
                    <TableRow key={exception.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(exception.start_date)} - {formatDate(exception.end_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {exception.adjusted_start_time} - {exception.adjusted_end_time}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {REASON_LABELS[exception.reason] || exception.reason}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[exception.status]}`}>
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_LABELS[exception.status] || exception.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {exception.approved_by_name || '-'}
                        {exception.rejection_comment && (
                          <p className="text-xs text-red-600 mt-1">
                            Motif: {exception.rejection_comment}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {exception.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleCancel(exception.id)}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
